import client from 'client'
import type {
  RetireAssetAssetAssetIdRetirePostMutationRequest,
  RetireAssetAssetAssetIdRetirePostMutationResponse,
  RetireAssetAssetAssetIdRetirePostPathParams,
  RetireAssetAssetAssetIdRetirePostQueryParams,
  RetireAssetAssetAssetIdRetirePost422,
} from '../../types/RetireAssetAssetAssetIdRetirePost.ts'
import type { UseMutationOptions } from '@tanstack/react-query'
import type { RequestConfig, ResponseConfig, ResponseErrorConfig } from 'client'
import { useMutation } from '@tanstack/react-query'

export const retireAssetAssetAssetIdRetirePostMutationKey = () => [{ url: '/asset/{asset_id}/retire' }] as const

export type RetireAssetAssetAssetIdRetirePostMutationKey = ReturnType<typeof retireAssetAssetAssetIdRetirePostMutationKey>

/**
 * @description Retire an asset with a specified retirement date.    The retirement date must:    - Be today or in the past    - Be at midnight (00:00:00) in Europe/London timezone    Retired assets cannot be assigned to positions after their retirement date.
 * @summary Retire an asset
 * {@link /asset/:asset_id/retire}
 */
export async function retireAssetAssetAssetIdRetirePost(
  asset_id: RetireAssetAssetAssetIdRetirePostPathParams['asset_id'],
  data: RetireAssetAssetAssetIdRetirePostMutationRequest,
  params?: RetireAssetAssetAssetIdRetirePostQueryParams,
  config: Partial<RequestConfig<RetireAssetAssetAssetIdRetirePostMutationRequest>> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<
    RetireAssetAssetAssetIdRetirePostMutationResponse,
    ResponseErrorConfig<RetireAssetAssetAssetIdRetirePost422>,
    RetireAssetAssetAssetIdRetirePostMutationRequest
  >({ method: 'POST', url: `/asset/${asset_id}/retire`, params, data, ...requestConfig })
  return res
}

/**
 * @description Retire an asset with a specified retirement date.    The retirement date must:    - Be today or in the past    - Be at midnight (00:00:00) in Europe/London timezone    Retired assets cannot be assigned to positions after their retirement date.
 * @summary Retire an asset
 * {@link /asset/:asset_id/retire}
 */
export function useRetireAssetAssetAssetIdRetirePost(
  options: {
    mutation?: UseMutationOptions<
      ResponseConfig<RetireAssetAssetAssetIdRetirePostMutationResponse>,
      ResponseErrorConfig<RetireAssetAssetAssetIdRetirePost422>,
      {
        asset_id: RetireAssetAssetAssetIdRetirePostPathParams['asset_id']
        data: RetireAssetAssetAssetIdRetirePostMutationRequest
        params?: RetireAssetAssetAssetIdRetirePostQueryParams
      }
    >
    client?: Partial<RequestConfig<RetireAssetAssetAssetIdRetirePostMutationRequest>> & { client?: typeof client }
  } = {},
) {
  const { mutation: mutationOptions, client: config = {} } = options ?? {}
  const mutationKey = mutationOptions?.mutationKey ?? retireAssetAssetAssetIdRetirePostMutationKey()

  return useMutation<
    ResponseConfig<RetireAssetAssetAssetIdRetirePostMutationResponse>,
    ResponseErrorConfig<RetireAssetAssetAssetIdRetirePost422>,
    {
      asset_id: RetireAssetAssetAssetIdRetirePostPathParams['asset_id']
      data: RetireAssetAssetAssetIdRetirePostMutationRequest
      params?: RetireAssetAssetAssetIdRetirePostQueryParams
    }
  >({
    mutationFn: async ({ asset_id, data, params }) => {
      return retireAssetAssetAssetIdRetirePost(asset_id, data, params, config)
    },
    mutationKey,
    ...mutationOptions,
  })
}